import { useState, useRef, useEffect } from 'react';
import { TextField, InputAdornment, Button, CircularProgress, Box, Paper, Typography, Alert } from '@mui/material';
import { Send, StarFourPoints } from '../svgUrls';
import { textFieldSX } from '../constants/textFieldCSS';
import { ChatMessage, useChatWorkflow } from '../api/chatWorkflowApi';

interface ChatInterfaceProps {
  onWorkflowReady: (description: string) => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ onWorkflowReady }) => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isWorkflowReady, setIsWorkflowReady] = useState(false);
  const [finalWorkflowDescription, setFinalWorkflowDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isCreatingAgent, setIsCreatingAgent] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { mutate: sendChatMessage, isPending } = useChatWorkflow();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Check if the response indicates a workflow is ready
  const checkIfWorkflowReady = (response: string): boolean => {
    return (
      response.includes("Here's what I've got for your workflow:") || 
      response.includes("Does this look good to you?") ||
      response.includes("Starts when:") ||
      (response.includes("Then:") && response.includes("1."))
    );
  };

  const handleSendMessage = () => {
    if (!input.trim()) return;
    setError(null);

    // Add user message to chat
    const userMessage: ChatMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    
    // Clear input
    setInput('');

    // Send to API
    sendChatMessage(
      { prompt: input, history: messages },
      {
        onSuccess: (response) => {
          // Add AI response to chat
          const aiMessage: ChatMessage = { role: 'assistant', content: response };
          setMessages(prev => [...prev, aiMessage]);

          // Check if workflow is ready
          const isReady = checkIfWorkflowReady(response);
          
          if (isReady) {
            setIsWorkflowReady(true);
            setFinalWorkflowDescription(response);
          }
        },
        onError: (error) => {
          setError('Failed to get a response. Please try again.');
          console.error('Chat API error:', error);
        }
      }
    );
  };

  const handleCreateAgent = () => {
    setIsCreatingAgent(true);
    try {
      onWorkflowReady(finalWorkflowDescription);
    } catch (err) {
      console.error('Error creating agent:', err);
      setError('Failed to create agent. Please try again.');
      setIsCreatingAgent(false);
    }
  };

  const formatMessage = (content: string): string => {
    // Replace markdown-style bullet points with HTML
    return content
      .replace(/\n- /g, '\n• ')
      .replace(/\n  \d+\. /g, '\n    ○ ');
  };

  return (
    <Box sx={{ 
      width: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      mb: 4,
      minHeight: messages.length > 0 ? '400px' : 'auto'
    }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Tell us what you want to automate
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {/* Chat messages */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2, 
          mb: 2, 
          height: messages.length > 0 ? '350px' : '0px',
          maxHeight: '350px',
          overflowY: 'auto',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          transition: 'height 0.3s ease-in-out',
          display: messages.length > 0 ? 'block' : 'none'
        }}
      >
        {messages.map((message, index) => (
          <Box 
            key={index} 
            sx={{ 
              mb: 2, 
              display: 'flex',
              flexDirection: 'column',
              alignItems: message.role === 'user' ? 'flex-end' : 'flex-start'
            }}
          >
            <Box 
              sx={{ 
                p: 2, 
                borderRadius: '12px',
                backgroundColor: message.role === 'user' ? '#546FFF' : '#ffffff',
                color: message.role === 'user' ? '#ffffff' : 'inherit',
                maxWidth: '80%',
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)'
              }}
            >
              <Typography 
                variant="body1" 
                sx={{ 
                  whiteSpace: 'pre-wrap',
                  '& a': { color: '#546FFF', textDecoration: 'none' }
                }}
                dangerouslySetInnerHTML={{ 
                  __html: formatMessage(message.content) 
                }}
              />
            </Box>
          </Box>
        ))}
        <div ref={messagesEndRef} />
      </Paper>

      {/* Create Agent button */}
      {isWorkflowReady && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Button 
            variant="contained" 
            onClick={handleCreateAgent}
            disabled={isCreatingAgent}
            sx={{ 
              textTransform: 'none', 
              borderRadius: '8px',
              backgroundColor: '#546FFF',
              padding: '8px 24px',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#3a56e8'
              },
              '&.Mui-disabled': {
                backgroundColor: '#8c9eff',
                color: 'white'
              }
            }}
            startIcon={isCreatingAgent ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isCreatingAgent ? 'Creating Agent...' : 'Create Agent'}
          </Button>
        </Box>
      )}

      {/* Input field */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <TextField
          fullWidth
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage();
            }
          }}
          placeholder="Tell us what you want to automate..."
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {isPending ? (
                  <CircularProgress size={24} />
                ) : (
                  <Send 
                    onClick={handleSendMessage} 
                    style={{ cursor: 'pointer' }}
                  />
                )}
              </InputAdornment>
            )
          }}
          className="bg-white rounded-lg"
          sx={{
            ...textFieldSX
          }}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          multiline
          maxRows={3}
          disabled={isPending || isCreatingAgent}
        />
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Ex: Import files from Google Drive and send to Quickbooks.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <StarFourPoints />
          <Typography variant="caption" sx={{ ml: 0.5 }} color="text.secondary">
            Powered by Nanonets AI
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatInterface; 